import { createRootRouteWithContext, createRoute, createRouter, lazyRouteComponent, Link, redirect, type RouteComponent } from '@tanstack/react-router'
import type { QueryClient } from '@tanstack/react-query'
import { z } from 'zod'
import { FORM_STATUSES } from '@/contract'
import { AppShell } from '@/components/shell/AppShell'
import { secondaryButton } from '@/components/shell/StatusScreens'
import { CHURCH_VIEWS, CORP_VIEWS, EMB_VIEWS, REDE_BASES, type SubView } from '@/features/admin/rede/nav'

/* Spec COG-005 — route tree. Início, Agentes, Conexões (phase 1), Precisa de você (phase 2),
   Fichas and Ao Vivo (phase 3), Pergunte and Memória (phase 4), Mundo 3D. The session gate (GET /me) lives in AppShell. */

const HomePage = lazyRouteComponent(() => import('@/features/home/HomePage'), 'HomePage')
const AgentsPage = lazyRouteComponent(() => import('@/features/agents/AgentsPage'), 'AgentsPage')
const ConnectionsPage = lazyRouteComponent(() => import('@/features/connections/ConnectionsPage'), 'ConnectionsPage')
const ReviewPage = lazyRouteComponent(() => import('@/features/review/ReviewPage'), 'ReviewPage')
const FormsPage = lazyRouteComponent(() => import('@/features/forms/FormsPage'), 'FormsPage')
const LivePage = lazyRouteComponent(() => import('@/features/live/LivePage'), 'LivePage')
const AskPage = lazyRouteComponent(() => import('@/features/ask/AskPage'), 'AskPage')
const MemoryPage = lazyRouteComponent(() => import('@/features/memory/MemoryPage'), 'MemoryPage')
// The 3D world (three.js) loads only when /mundo opens
const UsagePage = lazyRouteComponent(() => import('@/features/usage/UsagePage'), 'UsagePage')
const WorldPage = lazyRouteComponent(() => import('@/features/world/WorldPage'), 'WorldPage')

// COG-009: Admin (menu Admin replaces the main rail under /admin, see AppShell)
const AdminHojePage = lazyRouteComponent(() => import('@/features/admin/hoje/HojePage'), 'HojePage')
const AdminFichasPage = lazyRouteComponent(() => import('@/features/admin/fichas/FichasPage'), 'FichasPage')
const AdminPreAtivosPage = lazyRouteComponent(() => import('@/features/admin/pre-ativos/PreAtivosPage'), 'PreAtivosPage')
const AdminRecorrenciasPage = lazyRouteComponent(() => import('@/features/admin/recorrencias/RecorrenciasPage'), 'RecorrenciasPage')
const AdminRetencaoPage = lazyRouteComponent(() => import('@/features/admin/retencao/RetencaoPage'), 'RetencaoPage')
const AdminConciliacaoPage = lazyRouteComponent(() => import('@/features/admin/conciliacao/ConciliacaoPage'), 'ConciliacaoPage')
const AdminModeracaoPage = lazyRouteComponent(() => import('@/features/admin/moderacao/ModeracaoPage'), 'ModeracaoPage')
const AdminCartasPage = lazyRouteComponent(() => import('@/features/admin/cartas/CartasPage'), 'CartasPage')
const AdminVerComoPage = lazyRouteComponent(() => import('@/features/admin/ver-como/VerComoPage'), 'VerComoPage')
const AdminParceirosPage = lazyRouteComponent(() => import('@/features/admin/parceiros/ParceirosPage'), 'ParceirosPage')
const AdminEmbaixadoresPage = lazyRouteComponent(() => import('@/features/admin/embaixadores/EmbaixadoresPage'), 'EmbaixadoresPage')
// ERP (frentes, fichas ligadas, Início, Configurações)
const ErpInicioPage = lazyRouteComponent(() => import('@/features/erp/inicio/InicioPage'), 'InicioPage')
const ErpViewPage = lazyRouteComponent(() => import('@/features/erp/views/ErpViewPage'), 'ErpViewPage')
const ErpFichaPage = lazyRouteComponent(() => import('@/features/erp/fichas/FichaPage'), 'FichaPage')
const ErpExperiencePage = lazyRouteComponent(() => import('@/features/erp/xp/ExperiencePage'), 'ExperiencePage')
const ErpStaffEntry = lazyRouteComponent(() => import('@/features/erp/xp/StaffEntry'), 'StaffEntry')
const ErpConfigPage = lazyRouteComponent(() => import('@/features/erp/config/ConfigPage'), 'ConfigPage')
const AdminEmbaixadoresPortalPage = lazyRouteComponent(() => import('@/features/admin/embaixadores/PortalPage'), 'PortalPage')
const AdminEmpresasPage = lazyRouteComponent(() => import('@/features/admin/parceiros/ParceirosPage'), 'EmpresasPage')
const AdminIgrejasPage = lazyRouteComponent(() => import('@/features/admin/parceiros/ParceirosPage'), 'IgrejasPage')
const AdminInscricoesPage = lazyRouteComponent(() => import('@/features/admin/inscricoes/InscricoesPage'), 'InscricoesPage')
const AdminMarketingPage = lazyRouteComponent(() => import('@/features/admin/marketing/MarketingPage'), 'MarketingPage')
const AdminIndicadoresPage = lazyRouteComponent(() => import('@/features/admin/indicadores/IndicadoresPage'), 'IndicadoresPage')
const AdminEquipePage = lazyRouteComponent(() => import('@/features/admin/equipe/EquipePage'), 'EquipePage')
const AdminDenunciaPage = lazyRouteComponent(() => import('@/features/admin/denuncia/DenunciaPage'), 'DenunciaPage')

type RouterContext = { queryClient: QueryClient }

function NotFound() {
    return (
        <div className="flex flex-col items-start gap-3">
            <h1 className="m-0 text-[24px] font-bold">Página não encontrada</h1>
            <Link to="/" className={secondaryButton}>Voltar ao Início</Link>
        </div>
    )
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
    component: AppShell,
    notFoundComponent: NotFound,
})

const homeRoute = createRoute({ getParentRoute: () => rootRoute, path: '/', component: HomePage })

const agentsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/agentes',
    validateSearch: z.object({
        agente: z.string().regex(/^[a-z0-9-]+$/).optional().catch(undefined),
        // Cartões (default) or the org chart
        visao: z.enum(['organograma']).optional().catch(undefined),
    }),
    component: AgentsPage,
})

const connectionsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/conexoes',
    validateSearch: z.object({ sistema: z.string().regex(/^[a-z0-9-]+$/).optional().catch(undefined) }),
    component: ConnectionsPage,
})

const page = <TPath extends string>(path: TPath, component: typeof HomePage) =>
    createRoute({
        getParentRoute: () => rootRoute,
        path,
        validateSearch: z.object({ agente: z.string().regex(/^[a-z0-9-]+$/).optional().catch(undefined) }),
        component,
    })

const liveRoute = page('/ao-vivo', LivePage)
const reviewRoute = page('/revisao', ReviewPage)
// Fichas accepts ?status= (e.g. "Ver cobradas" in Precisa de você opens the charged ones)
const formsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/fichas',
    validateSearch: z.object({
        agente: z.string().regex(/^[a-z0-9-]+$/).optional().catch(undefined),
        status: z.enum(FORM_STATUSES).optional().catch(undefined),
    }),
    component: FormsPage,
})
const askRoute = page('/pergunte', AskPage)
const memoryRoute = page('/memoria', MemoryPage)
const usageRoute = page('/consumo', UsagePage)
const worldRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/mundo',
    validateSearch: z.object({ area: z.string().regex(/^[a-z0-9-]+$/).optional().catch(undefined) }),
    component: WorldPage,
})

// /staff (e o login): a experiência de entrada da frente da pessoa
const adminIndexRoute = createRoute({ getParentRoute: () => rootRoute, path: '/staff', component: ErpStaffEntry })
// ERP: Início da frente, visão de cada experiência, telas novas (/staff/erp/<tela>), fichas ligadas e Configurações do ERP (só admin)
const erpInicioRoute = createRoute({ getParentRoute: () => rootRoute, path: '/staff/inicio', component: ErpInicioPage })
const erpExperienceRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/staff/xp/$xp',
    // tab: as padrão (hoje, ia, carteira) ou as próprias da experiência (xp/registry.ts); desconhecida = a primeira
    validateSearch: z.object({ tab: z.string().regex(/^[a-z0-9-]{1,30}$/).optional().catch(undefined) }),
    component: ErpExperiencePage,
})
const erpViewRoute = createRoute({ getParentRoute: () => rootRoute, path: '/staff/erp/$view', component: ErpViewPage })
const erpFichaRoute = createRoute({ getParentRoute: () => rootRoute, path: '/staff/ficha/$type/$id', component: ErpFichaPage })
// Criar a senha pelo link de acesso (sem sessão; quem desenha é o AppShell)
const setPasswordRoute = createRoute({ getParentRoute: () => rootRoute, path: '/definir-senha', component: () => null })
const erpConfigRoute = createRoute({ getParentRoute: () => rootRoute, path: '/configuracoes/$section', component: ErpConfigPage })
// Endereços antigos (antes do nome Staff): /admin e /admin/<tela>?<busca> levam ao mesmo lugar em /staff
const legacyAdminRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/admin',
    beforeLoad: () => {
        throw redirect({ to: '/staff/hoje' })
    },
})
const legacyAdminSplatRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/admin/$',
    beforeLoad: ({ params, location }) => {
        throw redirect({ href: `/staff/${params._splat ?? ''}${location.searchStr ?? ''}`, replace: true })
    },
})
const adminHojeRoute = createRoute({ getParentRoute: () => rootRoute, path: '/staff/hoje', component: AdminHojePage })
const adminFichasRoute = createRoute({ getParentRoute: () => rootRoute, path: '/staff/fichas', component: AdminFichasPage })
const adminPreAtivosRoute = createRoute({ getParentRoute: () => rootRoute, path: '/staff/pre-ativos', component: AdminPreAtivosPage })
const adminRecorrenciasRoute = createRoute({ getParentRoute: () => rootRoute, path: '/staff/recorrencias', component: AdminRecorrenciasPage })
const adminRetencaoRoute = createRoute({ getParentRoute: () => rootRoute, path: '/staff/retencao', component: AdminRetencaoPage })
const adminConciliacaoRoute = createRoute({ getParentRoute: () => rootRoute, path: '/staff/conciliacao', component: AdminConciliacaoPage })
const adminModeracaoRoute = createRoute({ getParentRoute: () => rootRoute, path: '/staff/moderacao', component: AdminModeracaoPage })
const adminCartasRoute = createRoute({ getParentRoute: () => rootRoute, path: '/staff/cartas', component: AdminCartasPage })
const adminVerComoRoute = createRoute({ getParentRoute: () => rootRoute, path: '/staff/ver-como', component: AdminVerComoPage })
const adminParceirosRoute = createRoute({ getParentRoute: () => rootRoute, path: '/staff/parceiros', component: AdminParceirosPage })
/* Rede: Embaixadores, Empresas e Igrejas têm sub-telas como no menu dos protótipos (/staff/<área>/<sub-tela>; o
   endereço da área leva à Visão geral). Uma rota só por área, para a busca e a régua aberta valerem entre as sub-telas. */
function subViewRoutes<B extends string>(base: B, views: readonly SubView[], component: RouteComponent) {
    const first = `${base}/${views[0]!.slug}`
    return [
        createRoute({ getParentRoute: () => rootRoute, path: base, beforeLoad: () => { throw redirect({ href: first, replace: true }) } }),
        createRoute({
            getParentRoute: () => rootRoute,
            path: `${base}/$view` as `${B}/$view`,
            beforeLoad: ({ params }) => {
                if (!views.some(v => v.slug === (params as { view: string }).view)) throw redirect({ href: first, replace: true })
            },
            component,
        }),
    ] as const
}
const adminEmbaixadoresRoutes = subViewRoutes(REDE_BASES.embaixadores, EMB_VIEWS, AdminEmbaixadoresPage)
const adminEmpresasRoutes = subViewRoutes(REDE_BASES.empresas, CORP_VIEWS, AdminEmpresasPage)
const adminIgrejasRoutes = subViewRoutes(REDE_BASES.igrejas, CHURCH_VIEWS, AdminIgrejasPage)
// Portal do cliente (aprovação de embaixadores): tela própria, sem o menu do Staff (AppShell)
const adminEmbaixadoresPortalRoute = createRoute({ getParentRoute: () => rootRoute, path: '/staff/embaixadores/portal', component: AdminEmbaixadoresPortalPage })
const adminInscricoesRoute = createRoute({ getParentRoute: () => rootRoute, path: '/staff/inscricoes', component: AdminInscricoesPage })
const adminMarketingRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/staff/marketing',
    validateSearch: z.object({ aba: z.enum(['campanhas', 'roi']).optional().catch(undefined) }),
    component: AdminMarketingPage,
})
const adminIndicadoresRoute = createRoute({ getParentRoute: () => rootRoute, path: '/staff/indicadores', component: AdminIndicadoresPage })
const adminEquipeRoute = createRoute({ getParentRoute: () => rootRoute, path: '/staff/equipe', component: AdminEquipePage })
const adminDenunciaRoute = createRoute({ getParentRoute: () => rootRoute, path: '/staff/denuncia', component: AdminDenunciaPage })

const routeTree = rootRoute.addChildren([
    legacyAdminRoute,
    legacyAdminSplatRoute,
    homeRoute, liveRoute, reviewRoute, agentsRoute, formsRoute, askRoute, memoryRoute, connectionsRoute, usageRoute, worldRoute,
    adminIndexRoute, erpInicioRoute, erpExperienceRoute, erpViewRoute, erpFichaRoute, erpConfigRoute, setPasswordRoute, adminHojeRoute, adminFichasRoute, adminPreAtivosRoute, adminRecorrenciasRoute, adminRetencaoRoute, adminConciliacaoRoute, adminModeracaoRoute, adminCartasRoute, adminVerComoRoute, adminParceirosRoute, ...adminEmbaixadoresRoutes, adminEmbaixadoresPortalRoute, ...adminEmpresasRoutes, ...adminIgrejasRoutes, adminInscricoesRoute, adminMarketingRoute, adminIndicadoresRoute, adminEquipeRoute, adminDenunciaRoute,
])

export function createAppRouter(queryClient: QueryClient) {
    return createRouter({ routeTree, context: { queryClient }, defaultPreload: 'intent', scrollRestoration: true })
}

declare module '@tanstack/react-router' {
    interface Register {
        router: ReturnType<typeof createAppRouter>
    }
}
